class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Track keyboard closes (Esc) so close() can tell them apart from
    // pointer closes; keydown fires before the keyup close handler below.
    this.closedByKeyboard = false;
    this.addEventListener('keydown', (event) => {
      if (event.code === 'Escape') this.closedByKeyboard = true;
    });

    // Remember where the pointer last was inside the drawer (X button or
    // overlay) so close() can tell which product card sits under it.
    this.lastPointerPosition = null;
    this.addEventListener('pointerup', (event) => {
      this.lastPointerPosition = { x: event.clientX, y: event.clientY };
    });

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', (event) => {
      if (event.target.closest('.ws-cart-close, .ws-cart-empty__btn')) this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // .ws-cart-empty is this theme's empty state; fall back to the drawer
        // itself so the trap never receives a null container.
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty') ||
            this.querySelector('.ws-cart-empty') ||
            document.getElementById('CartDrawer')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    const opener = this.activeElement;
    // removeTrapFocus() refocuses the opener. When that's a card's hidden
    // "Choose Options" button (quick-add → add to cart → drawer), focus()
    // scrolls the card's overflow-hidden .card__inner and leaves its layout
    // permanently shifted — capture the in-card scroll state and restore it
    // after (same fix as quick-add.js).
    const openerCard = opener && opener.closest ? opener.closest('.card-wrapper') : null;
    const scrollState = [];
    if (openerCard) {
      for (let el = opener.parentElement; el; el = el.parentElement) {
        scrollState.push([el, el.scrollTop, el.scrollLeft]);
        if (el === openerCard) break;
      }
    }
    removeTrapFocus(this.activeElement);
    scrollState.forEach(([el, top, left]) => {
      el.scrollTop = top;
      el.scrollLeft = left;
    });
    if (openerCard && !this.closedByKeyboard) {
      // Pointer close: release focus so the card's focus-reveal doesn't pin
      // the button, and suppress hover effects on the card under the pointer
      // until it genuinely leaves (cleared on mouseleave only — the browser
      // fires a synthetic mouseenter when the drawer vanishes).
      opener.blur();
      if (this.lastPointerPosition) {
        const under = document.elementFromPoint(this.lastPointerPosition.x, this.lastPointerPosition.y);
        const card = under && under.closest('.card-wrapper');
        if (card) {
          card.classList.add('ws-qa-hover-reset');
          card.addEventListener('mouseleave', () => card.classList.remove('ws-qa-hover-reset'), { once: true });
        }
      }
    }
    this.closedByKeyboard = false;
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (newHTML !== null) sectionElement.innerHTML = newHTML;
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
